<template>
  <main class="main">
    <form class="form" @submit.prevent="utilisateur.id ? updateUtilisateur() : createUtilisateur()">
      <fieldset class="fieldset">
        <legend class="legend">
          <PhIdentificationCard />
          <span>Informations</span>
        </legend>

        <div class="field">
          <label class="label">Nom</label>
          <input type="text" class="input" v-model="utilisateur.nom" required />
        </div>

        <div class="field">
          <label class="label">Prénom</label>
          <input type="text" class="input" v-model="utilisateur.prenom" required />
        </div>

        <div class="field">
          <label class="label">Identifiant</label>
          <input type="text" class="input" v-model="utilisateur.identifiant" required />
        </div>

        <div class="field">
          <label class="label">Email</label>
          <input type="email" class="input" v-model="utilisateur.email" />
        </div>

        <div class="field">
          <label class="label">Rôle</label>
          <select class="select" v-model="utilisateur.role" required>
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.nom }}
            </option>
          </select>
        </div>

        <div class="field" v-if="utilisateurId">
          <label class="label">Actif ?</label>
          <input type="checkbox" class="checkbox" v-model="utilisateur.actif" />
        </div>

        <div class="field" v-if="hasCode">
          <label class="label">Code</label>
          <p>
            <b>{{ utilisateur.code }}</b
            ><br /><br />
            Ce code permet à l’utilisateur d’enregistrer une nouvelle clé d'accès.<br />
            Il expirera automatiquement à
            {{ utilisateur.code_expiration ? frenchTime(utilisateur.code_expiration) : '' }} et ne
            peut être utilisé qu’une seule fois.
          </p>
        </div>
      </fieldset>
      <div class="buttons">
        <ButtonElement
          type="submit"
          color="bordeaux"
          :icon="PhFloppyDisk"
          :disabled="!cacheStore.online"
        >
          Enregistrer
        </ButtonElement>
        <ButtonElement
          v-if="utilisateurId"
          type="button"
          color="bordeaux"
          bordered
          :disabled="!cacheStore.online"
          :icon="PhClockCountdown"
          @click="createCodeUtilisateur"
        >
          Générer un code
        </ButtonElement>
      </div>
    </form>
  </main>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { computed, type ComputedRef, onMounted, type Ref, ref } from 'vue'
import useFetch from '@/composables/useFetch.ts'
import type RoleList from '@/interfaces/Role.ts'
import { PhClockCountdown, PhFloppyDisk, PhIdentificationCard } from '@phosphor-icons/vue'
import type { UtilisateurGet } from '@/interfaces/Utilisateur.ts'
import ButtonElement from '@/components/ButtonElement.vue'
import { useLoadingStore } from '@/stores/loading.ts'
import { useToastStore } from '@/stores/toast.ts'
import frenchTime from '@/mixins/getFrenchTime.ts'
import { useCacheStore } from '@/stores/cache.ts'

// dépendances
const api = useFetch()
const route = useRoute()
const router = useRouter()

// stores
const cacheStore = useCacheStore()
const loadingStore = useLoadingStore()
const toastStore = useToastStore()

const utilisateur: Ref<UtilisateurGet> = ref({
  actif: true,
})
const roles: Ref<RoleList[]> = ref([])

const utilisateurId: ComputedRef<string | undefined> = computed(() => route.params.id?.toString())

const hasCode = computed(
  () =>
    utilisateur.value?.code &&
    utilisateur.value?.code_expiration &&
    new Date() < new Date(utilisateur.value.code_expiration),
)
async function getRoles() {
  roles.value = await cacheStore.getRoles()
}
async function getUtilisateur() {
  if (utilisateurId.value) utilisateur.value = await cacheStore.getUtilisateur(utilisateurId.value)
}
async function updateUtilisateur() {}
async function createUtilisateur() {
  loadingStore.start()
  try {
    await api.post(`utilisateurs/`, utilisateur.value)
    toastStore.success("L'utilisateur a bien été créé")
    await router.push({ name: 'utilisateurs' })
  } finally {
    loadingStore.stop()
  }
}
async function createCodeUtilisateur() {
  loadingStore.start()
  try {
    const response = await api.post(`utilisateurs/${utilisateur.value?.id}/code`)
    utilisateur.value.code = response.code
    utilisateur.value.code_expiration = response.code_expiration
    const code = `${response.code.substring(0, 2)} ${response.code.substring(2, 4)} ${response.code.substring(4, 6)}`
    const expiration = frenchTime(response.code_expiration)
    toastStore.info(
      `Le code d'invitation est <b>${code}</b>,<br>valable jusqu'à ${expiration}.`,
      30000,
    )
  } finally {
    loadingStore.stop()
  }
}

onMounted(async () => {
  loadingStore.start()
  try {
    await getRoles()
    await getUtilisateur()
  } finally {
    loadingStore.stop()
  }
})
</script>
